import { PinchGesture } from '@use-gesture/vanilla';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { KeyboardManager } from './useKeyboardManager';

const MAX_ZOOM = 3;
const ZOOM_OFFSET = 0.1;

interface MousePosition {
  x: number;
  y: number;
}

export const useZoom = (options: {
  keyboardManager: KeyboardManager;
  scrollableRef?: MutableRefObject<HTMLDivElement>;
  zoomContainerRef?: MutableRefObject<HTMLDivElement>;
  defaultZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  onZoom?: (zoom: number) => void;
}) => {
  const defaultZoom = options.defaultZoom || 1;
  const onZoom = options.onZoom;
  const zoomRef = useRef(defaultZoom);
  const [zoom, setZoom] = useState(defaultZoom);
  const mousePositionRef = useRef<MousePosition | null>(null);
  const { keyboardManager, scrollableRef, zoomContainerRef } = options;
  const addEventListener = keyboardManager.addEventListener;
  const minZoom = options.minZoom ?? 1;
  const maxZoom = options.maxZoom ?? MAX_ZOOM;

  const updateZoom = useCallback(
    (zoom: number, focusPoint?: MousePosition) => {
      const scrollableElem = scrollableRef?.current;
      const zoomContainer = zoomContainerRef?.current;

      if (!zoomContainer || !scrollableElem) return;

      const scrollLeft = scrollableElem.scrollLeft;
      const scrollTop = scrollableElem.scrollTop;
      const prevZoom = zoomRef.current;

      if (zoom < minZoom) {
        zoomRef.current = minZoom;
      } else if (zoom > maxZoom) {
        zoomRef.current = maxZoom;
      } else {
        zoomRef.current = zoom;
      }

      setZoom(zoomRef.current);

      if (zoomContainer) {
        zoomContainer.style.transform = `scale(${zoomRef.current})`;
      }

      const zoomRatio = zoomRef.current / prevZoom;

      if (zoomRatio !== minZoom) {
        if (focusPoint) {
          // Reposition scroll to keep the focus point (mouse position) stable
          // The point under the mouse in document coords: (scrollLeft + mouseX, scrollTop + mouseY)
          // After zoom, that becomes: ((scrollLeft + mouseX) * zoomRatio, (scrollTop + mouseY) * zoomRatio)
          // We want that point to still appear at (mouseX, mouseY) on screen
          const newScrollLeft =
            (scrollLeft + focusPoint.x) * zoomRatio - focusPoint.x;
          const newScrollTop =
            (scrollTop + focusPoint.y) * zoomRatio - focusPoint.y;

          scrollableElem.scrollTo({
            left: newScrollLeft,
            top: newScrollTop,
          });
        } else {
          scrollableElem.scrollTo({
            top: scrollTop * zoomRatio,
          });
        }
      }

      window.requestAnimationFrame(() => {
        onZoom && onZoom(zoomRef.current);
      });
    },
    [scrollableRef, zoomContainerRef, minZoom, maxZoom, onZoom]
  );

  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', handler);
    document.addEventListener('gesturechange', handler);
    document.addEventListener('gestureend', handler);

    return () => {
      document.removeEventListener('gesturestart', handler);
      document.removeEventListener('gesturechange', handler);
      document.removeEventListener('gestureend', handler);
    };
  }, []);

  useEffect(() => {
    if (zoomContainerRef?.current) {
      zoomContainerRef.current.style.transform = `scale(${zoomRef.current})`;
    }
  }, [zoomContainerRef]);

  // Track mouse position over the scrollable element
  useEffect(() => {
    const scrollableElem = scrollableRef?.current;
    if (!scrollableElem) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = scrollableElem.getBoundingClientRect();
      mousePositionRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const handleMouseLeave = () => {
      mousePositionRef.current = null;
    };

    scrollableElem.addEventListener('mousemove', handleMouseMove);
    scrollableElem.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      scrollableElem.removeEventListener('mousemove', handleMouseMove);
      scrollableElem.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [scrollableRef]);

  useEffect(() => {
    addEventListener(({ equal, minus, meta }) => {
      const currentZoom = zoomRef.current;

      if (!meta || (!equal && !minus)) {
        return;
      }

      const newZoom = equal
        ? currentZoom + ZOOM_OFFSET
        : currentZoom - ZOOM_OFFSET;

      // Use mouse position as focus point if available
      updateZoom(newZoom, mousePositionRef.current ?? undefined);
    }, 'useZoom');
  }, [addEventListener, updateZoom, zoomRef]);

  useEffect(() => {
    if (!scrollableRef?.current) return;

    const gesture = new PinchGesture(
      scrollableRef?.current,
      ({ origin, direction, last, delta, event }) => {
        const scrollableElem = scrollableRef?.current;
        const zoomContainer = zoomContainerRef?.current;
        const isWheel = event.type === 'wheel';

        const wheelDelta = direction[0] * ZOOM_OFFSET;

        if (
          last ||
          !zoomContainer ||
          !scrollableElem ||
          (zoomRef.current === maxZoom && direction[0] > 0) ||
          (zoomRef.current === minZoom && direction[0] < 0)
        )
          return;

        const { left, top } = scrollableElem.getBoundingClientRect();
        const prevZoom = zoomRef.current;
        const newZoom = prevZoom + (isWheel ? wheelDelta : delta[0]);

        // Use pinch origin as the focus point
        const focusPoint: MousePosition = {
          x: origin[0] - left,
          y: origin[1] - top,
        };

        updateZoom(newZoom, focusPoint);
      },
      {
        preventDefault: true,
        from: () => {
          return [zoomRef.current, 0];
        },
        pinchOnWheel: true,
        rubberband: true,
        modifierKey: 'metaKey',
        scaleBounds: {
          min: minZoom,
          max: maxZoom,
        },
      }
    );

    return () => {
      gesture.destroy();
    };
  }, [
    defaultZoom,
    scrollableRef,
    updateZoom,
    zoomContainerRef,
    minZoom,
    maxZoom,
  ]);

  return {
    zoom,
  };
};
